import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Forces the user to change their password on first login.
 * Also usable as a standalone dialog by passing controlled open state.
 */
export function ChangePasswordDialog({ forceOpen, onClose }) {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const mustChange = !!user?.must_change_password;

  useEffect(() => {
    if (typeof forceOpen === "boolean") setOpen(forceOpen);
    else if (mustChange) setOpen(true);
  }, [forceOpen, mustChange]);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("Las contraseñas no coinciden");
    if (next.length < 6) return toast.error("Mínimo 6 caracteres");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Contraseña actualizada");
      setCurrent(""); setNext(""); setConfirm("");
      setOpen(false);
      onClose?.();
      await refreshUser?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (mustChange ? null : setOpen(o))}>
      <DialogContent className="max-w-md" onEscapeKeyDown={(e) => mustChange && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{mustChange ? "Cambio de contraseña obligatorio" : "Cambiar contraseña"}</DialogTitle>
          <DialogDescription>
            {mustChange
              ? "Es la primera vez que inicia sesión. Elija una nueva contraseña para continuar."
              : "Ingrese su contraseña actual y la nueva."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Contraseña actual</Label>
            <Input data-testid="pwd-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div>
            <Label className="text-xs">Nueva contraseña</Label>
            <Input data-testid="pwd-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} required />
          </div>
          <div>
            <Label className="text-xs">Confirmar nueva contraseña</Label>
            <Input data-testid="pwd-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <DialogFooter>
            <Button data-testid="pwd-save" type="submit" disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
